/**
 * Market detection (PT default / BR). BR is active only on the Brazilian
 * domain, or in preview via ?market=br (persisted in sessionStorage,
 * cleared with ?market=pt). Portugal behaviour is unchanged by default.
 */
export type Market = "pt" | "br";

function detect(): Market {
  if (typeof window === "undefined") return "pt";
  const host = window.location.hostname.toLowerCase();
  if (host.includes("hexatechbrasil")) return "br";
  try {
    const q = new URLSearchParams(window.location.search).get("market");
    if (q === "br" || q === "pt") sessionStorage.setItem("hx_market", q);
    if (sessionStorage.getItem("hx_market") === "br") return "br";
  } catch { /* ignore */ }
  return "pt";
}

export const MARKET: Market = detect();
export const isBR = MARKET === "br";

/** Dados oficiais da empresa brasileira (comprovante CNPJ, 05/10/2026). */
export const BR_COMPANY = {
  name: "HEXATECH BRASIL LTDA",
  cnpj: "69.481.076/0001-82",
  addressLine1: "R Johann Sachse, 539 — Badenfurt",
  addressLine2: "Blumenau/SC — CEP 89.070-400",
  email: "romao.repre@gmail.com",
  phone: "+55 47 9222-0050",
  copyright: "HEXATECH BRASIL LTDA · CNPJ 69.481.076/0001-82. Todos os direitos reservados.",
} as const;

export function formatMoney(value: number | null | undefined): string {
  const v = Number(value || 0);
  return isBR
    ? v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
    : v.toLocaleString("pt-PT", { style: "currency", currency: "EUR" });
}

/**
 * In BR: keep only consumables enabled for Brazil with an R$ price and swap
 * price/stock to the BR values. In PT: returns rows untouched.
 */
export function applyMarket<T extends Record<string, any>>(rows: T[] | null | undefined): T[] {
  const list = rows ?? [];
  if (!isBR) return list;
  return list
    .filter((r) => r.visible_br !== false) // visibilidade BR independente
    .map((r) => {
      // Todo o catálogo aparece no Brasil; sem preço em R$ fica "Sob consulta".
       const brl: any = Number(r.price_brl ?? 0);
      const variants = Array.isArray(r.consumable_variants)
        ? r.consumable_variants
            .filter((v: any) => v.visible_br !== false)
             .map((v: any) => ({ ...v, price: Number(v.price_brl ?? 0), stock_quantity: Number(v.stock_quantity_br ?? 0) }))
        : r.consumable_variants;
      return { ...r, price: brl, stock_quantity: Number(r.stock_quantity_br ?? 0), consumable_variants: variants };
    });
}

export function applyMarketOne<T extends Record<string, any>>(row: T | null | undefined): T | null {
  if (!row) return null;
  return applyMarket([row])[0] ?? null;
}

/**
 * Pick the rows for the active storefront market from a PT+BR result set.
 * Never borrow operational configuration from another jurisdiction.
 */
export function pickMarketRows<T extends Record<string, any>>(rows: T[] | null | undefined, market: Market = MARKET): T[] {
  const list = rows ?? [];
  const own = list.filter((r) => (r.market ?? "pt") === market);
  return own;
}

/** Same as pickMarketRows but for singleton configs (first row wins). */
export function pickMarketRow<T extends Record<string, any>>(rows: T[] | null | undefined, market: Market = MARKET): T | null {
  return pickMarketRows(rows, market)[0] ?? null;
}
